import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import TeamLogo from "@/components/TeamLogo";
import { CalendarDays, MapPin, CloudRain, Trophy } from "lucide-react";

const FixturesTab = ({ fixtures, teamMap }) => {
  const [view, setView] = useState("upcoming"); // upcoming | results

  const completed = [...fixtures.completed].reverse();
  const washouts = fixtures.washed_out;
  const recent = [...completed, ...washouts].sort(
    (a, b) => (b.match_no || 0) - (a.match_no || 0)
  );

  return (
    <div className="space-y-3" data-testid="fixtures-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase tracking-tight text-xl">Schedule</h2>
        <div className="flex gap-1 bg-card border border-border rounded-sm p-0.5">
          <SegBtn active={view === "upcoming"} onClick={() => setView("upcoming")} testId="seg-upcoming">
            Upcoming · {fixtures.pending.length}
          </SegBtn>
          <SegBtn active={view === "results"} onClick={() => setView("results")} testId="seg-results">
            Results · {recent.length}
          </SegBtn>
        </div>
      </div>

      {view === "upcoming" ? (
        <div className="space-y-2" data-testid="upcoming-list">
          {fixtures.pending.map((f) => (
            <UpcomingRow key={f.id} f={f} teamMap={teamMap} />
          ))}
          {fixtures.pending.length === 0 && (
            <div className="text-sm text-muted-foreground py-4">No upcoming fixtures.</div>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="results-list">
          {recent.map((f) =>
            f.status === "washed_out" ? (
              <WashoutRow key={f.id} f={f} teamMap={teamMap} />
            ) : (
              <ResultRow key={f.id} f={f} teamMap={teamMap} />
            )
          )}
          {recent.length === 0 && (
            <div className="text-sm text-muted-foreground py-4">No results yet.</div>
          )}
        </div>
      )}
    </div>
  );
};

const SegBtn = ({ active, onClick, testId, children }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-display rounded-sm transition-colors ${
      active
        ? "bg-secondary text-primary"
        : "text-muted-foreground hover:text-foreground"
    }`}
  >
    {children}
  </button>
);

const UpcomingRow = ({ f, teamMap }) => (
  <Card
    className="bg-card border-border rounded-sm p-3 flex items-center gap-3"
    data-testid={`fixture-${f.id}`}
  >
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground w-12 shrink-0">
      <div className="flex items-center gap-1">
        <CalendarDays className="w-3 h-3" />
      </div>
      <div className="num text-foreground text-base leading-none mt-1">{f.match_no}</div>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <TeamLogo team={teamMap[f.home]} size={22} />
        <span className="font-display uppercase">{f.home}</span>
        <span className="text-muted-foreground text-xs">v</span>
        <span className="font-display uppercase">{f.away}</span>
        <TeamLogo team={teamMap[f.away]} size={22} />
      </div>
      <div className="text-[10px] text-muted-foreground mt-1 inline-flex items-center gap-1 truncate">
        <MapPin className="w-3 h-3 shrink-0" />
        <span className="truncate">{f.venue}</span>
      </div>
    </div>
    <div className="text-right">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Date</div>
      <div className="num text-sm">{f.date.slice(5)}</div>
    </div>
  </Card>
);

const ResultRow = ({ f, teamMap }) => {
  const r = f.result;
  const winnerIsHome = r.winner === f.home;
  const winnerIsAway = r.winner === f.away;
  return (
    <Card
      className="bg-card border-border rounded-sm p-3"
      data-testid={`result-${f.id}`}
    >
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
        <span>
          {f.id} · M{f.match_no}
        </span>
        <span className="inline-flex items-center gap-1 text-primary">
          <Trophy className="w-3 h-3" />
          {r.winner === "tie" ? "Tied" : r.winner === "no_result" ? "No result" : `${r.winner} won`}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <ScoreLine team={teamMap[f.home]} runs={r.home_runs} overs={r.home_overs} winner={winnerIsHome} />
        <ScoreLine team={teamMap[f.away]} runs={r.away_runs} overs={r.away_overs} winner={winnerIsAway} />
      </div>
      <div className="text-[10px] text-muted-foreground mt-2 truncate">
        {f.date} · {f.venue}
      </div>
    </Card>
  );
};

const ScoreLine = ({ team, runs, overs, winner }) => (
  <div
    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border ${
      winner ? "border-primary/40 bg-primary/5" : "border-border bg-secondary/30"
    }`}
  >
    <TeamLogo team={team} size={22} />
    <div className="flex-1 min-w-0">
      <div className="font-display uppercase text-sm leading-none">{team?.short}</div>
      <div className="text-[10px] text-muted-foreground truncate">{team?.name}</div>
    </div>
    <div className="text-right">
      <div className="num text-base leading-none">{runs}</div>
      <div className="text-[10px] text-muted-foreground num">({overs})</div>
    </div>
  </div>
);

const WashoutRow = ({ f, teamMap }) => (
  <Card className="bg-card border-border rounded-sm p-3" data-testid={`washout-${f.id}`}>
    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
      <span>
        {f.id} · M{f.match_no}
      </span>
      <span className="inline-flex items-center gap-1 text-amber-400">
        <CloudRain className="w-3 h-3" />
        Washed out
      </span>
    </div>
    <div className="flex items-center gap-2 mt-2">
      <TeamLogo team={teamMap[f.home]} size={20} />
      <span className="font-display uppercase">{f.home}</span>
      <span className="text-muted-foreground text-xs">v</span>
      <span className="font-display uppercase">{f.away}</span>
      <TeamLogo team={teamMap[f.away]} size={20} />
      <span className="ml-auto text-[10px] text-muted-foreground">1 pt each</span>
    </div>
  </Card>
);

export default FixturesTab;
